import { Stack, Typography } from "@mui/material";
import { Theme, useTheme } from '@mui/material/styles';
import axios from "axios";
import { useAtomValue } from "jotai";
import { FC, useEffect, useMemo, useState } from "react";
import { APP_ELEMENT_DIMENSIONS } from "../../constants";
import { baseAtom } from "../../stores/jotai/base";
import { scoringSelectedPeriodAtom } from "../../stores/jotai/scoring/selectedPeriodIndex";

interface OverviewStat {
    total_interviews: number;
    total_insights: number;
    total_hypo: number;
    total_hypo_val: number;
    total_hypo_inval: number;
}

type IData = Record<string, OverviewStat[]>;

interface IProgressTitleValue {
    title: string;
    value: number;
}

interface IProgressTitleValueMap {
    total_interviews: IProgressTitleValue;
    total_insights: IProgressTitleValue;
    total_hypo: IProgressTitleValue;
    total_hypo_val: IProgressTitleValue;
    total_hypo_inval: IProgressTitleValue;
}

const PROGRESS_DATA_CONTAINER_STYLES = (theme: Theme)=> ( {
    justifyContent: "space-between",
    gap: "48px",
    padding: "10px 16px",
    borderBottom: `1px solid ${theme?.custom?.disabledColor}`,
    flexShrink: 0,
});

const ScoringOverviewStats: FC = () => {
    const [chartData, setChartData] = useState<IData>(null);
    const [selectedWeekProgress, setSelectedWeekProgress] =
        useState<OverviewStat>(null);
    const selectedPeriodIndex = useAtomValue(scoringSelectedPeriodAtom);
    const base = useAtomValue(baseAtom);
    const chartDataUrl = base?.cohort?.chartDataUrl;
    const teamName = base?.team?.name;
    const theme = useTheme();
    const progressTitleValueMap: IProgressTitleValueMap = useMemo(
        () => ({
            total_interviews: {
                title: "New Interviews",
                value: selectedWeekProgress?.total_interviews || 0,
            },
            total_insights: {
                title: "New Insights",
                value: selectedWeekProgress?.total_insights || 0,
            },
            total_hypo: {
                title: "New Hypotheses",
                value: selectedWeekProgress?.total_hypo || 0,
            },
            total_hypo_val: {
                title: "Validated Hypotheses",
                value: selectedWeekProgress?.total_hypo_val || 0,
            },
            total_hypo_inval: {
                title: "Invalidated Hypotheses",
                value: selectedWeekProgress?.total_hypo_inval || 0,
            },
        }),
        [selectedWeekProgress]
    );

    useEffect(() => {
        if (!!chartDataUrl) {
            axios.get(chartDataUrl).then((response) => {
                setChartData(response?.data["stats_by_team_by_week"]);
            });
        }
    }, [chartDataUrl]);

    useEffect(() => {
        if (!!chartData) {
            const teamChartData = chartData[teamName];

            if (!!teamChartData) {
                setSelectedWeekProgress(teamChartData[selectedPeriodIndex]);
            }
        }
    }, [chartData, selectedPeriodIndex, teamName]);

    return (
        <Stack
            sx={{
                flexGrow: 1,
                width: APP_ELEMENT_DIMENSIONS.SCORING.OVERVIEW_STAT.WIDTH,
                borderLeft: `1px solid ${theme?.custom?.borderGrey}`,
                borderTop: `1px solid ${theme?.custom?.borderGrey}`,
                margin: "-1px 0 -1px -1px",
                backgroundColor: theme?.custom?.white,
            }}
        >
            <Stack
                direction="row"
                sx={{
                    ...PROGRESS_DATA_CONTAINER_STYLES(theme),
                    borderBottom: `1px solid ${theme?.custom?.borderGrey}`,
                }}
            >
                <Typography
                    align="center"
                    fontWeight={700}
                    sx={{
                        width: "100%",
                    }}
                >
                    Progress This Week
                </Typography>
            </Stack>
            {Object.values(progressTitleValueMap).map(
                (progress: IProgressTitleValue) => (
                    <Stack
                        key={progress?.title}
                        direction="row"
                        sx={PROGRESS_DATA_CONTAINER_STYLES(theme)}
                    >
                        <Typography sx={{ whiteSpace: "nowrap" }}>
                            {progress?.title}:
                        </Typography>{" "}
                        <Typography>{progress?.value}</Typography>
                    </Stack>
                )
            )}
        </Stack>
    );
};

export default ScoringOverviewStats;
